import { ArrowRight, DollarSign, FileText, FolderCheck, PieChart, ShieldCheck, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { StatusPill } from "@/components/StatusPill";
import { InitialsAvatar, VoiceBubble } from "./landingPrimitives";

/* ============================================================
 * §5 · HANDLE THE BUSINESS — slightly deeper warm office
 * ============================================================ */
export const HandleSection = ({ c }) => {
  const cards = [
    { icon: FileText,     title: c.handle.c1_title, body: c.handle.c1_body, testid: "handle-card-invoices" },
    { icon: PieChart,     title: c.handle.c2_title, body: c.handle.c2_body, testid: "handle-card-profitability" },
    { icon: FolderCheck,  title: c.handle.c3_title, body: c.handle.c3_body, testid: "handle-card-closeout" },
    { icon: ShieldCheck,  title: c.handle.c4_title, body: c.handle.c4_body, testid: "handle-card-warranties" },
  ];
  const value = [
    { icon: DollarSign,   t: c.handle.value[0].t, b: c.handle.value[0].b, testid: "handle-value-1" },
    { icon: PieChart,     t: c.handle.value[1].t, b: c.handle.value[1].b, testid: "handle-value-2" },
    { icon: FileText,     t: c.handle.value[2].t, b: c.handle.value[2].b, testid: "handle-value-3" },
    { icon: ShieldCheck,  t: c.handle.value[3].t, b: c.handle.value[3].b, testid: "handle-value-4" },
    { icon: TrendingUp,   t: c.handle.value[4].t, b: c.handle.value[4].b, testid: "handle-value-5" },
  ];
  const invoices = [
    { name: c.handle.inv_names.i1, amt: "$18,750.00", status: "paid",    label: c.handle.inv_paid },
    { name: c.handle.inv_names.i2, amt: "$14,200.00", status: "partial", label: c.handle.inv_partial },
    { name: c.handle.inv_names.i3, amt: "$4,850.00",  status: "sent",    label: c.handle.inv_unpaid },
  ];

  const RightUI = () => (
    <div className="space-y-3" data-testid="handle-right-stack">
      <VoiceBubble testid="handle-voice">{c.handle.voice}</VoiceBubble>
      <div className="rounded-xl bg-white border border-white shadow-lg p-4 flex items-start gap-3" data-testid="handle-reply">
        <InitialsAvatar initials="L" size={34} variant="navy" />
        <div className="text-[14.5px] lg:text-[13px] leading-snug text-[var(--jc-text)]">
          {c.handle.reply_pre}{" "}
          <span className="font-semibold">{c.handle.reply_mid}</span>
          {c.handle.reply_post}{" "}
          <span className="font-semibold text-[var(--jc-green)]">{c.handle.reply_profit}</span>
          {c.handle.reply_end}
        </div>
      </div>
      <div className="rounded-xl bg-white border border-white shadow-lg p-4" data-testid="handle-profitability">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[14.5px] lg:text-[13.5px] font-semibold text-[var(--jc-text)] leading-tight">{c.handle.panel_title}</div>
          <StatusPill status="completed" kind="project" t={(k) => (k === "status.completed" ? c.handle.panel_status : k)} />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-[12px] lg:text-[11px]">
          <div>
            <div className="text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.handle.panel_contract}</div>
            <div className="text-[16px] lg:text-[15px] font-semibold text-[var(--jc-text)]">$18,750</div>
          </div>
          <div>
            <div className="text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.handle.panel_actual}</div>
            <div className="text-[16px] lg:text-[15px] font-semibold text-[var(--jc-text)]">$12,820</div>
          </div>
          <div>
            <div className="text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.handle.panel_profit}</div>
            <div className="text-[16px] lg:text-[15px] font-semibold text-[var(--jc-green)]">$5,930</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div>
            <div className="text-[11.5px] lg:text-[10.5px] text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.handle.panel_margin_label}</div>
            <div className="text-[23px] lg:text-[22px] font-bold text-[var(--jc-blue)] leading-tight">31.6%</div>
          </div>
          <div className="flex-1">
            <svg viewBox="0 0 100 30" className="w-full h-8" aria-hidden>
              <polyline points="0,22 10,20 20,18 30,19 40,15 50,14 60,10 70,12 80,7 90,6 100,3" fill="none" stroke="var(--jc-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[12px] lg:text-[11px] pt-3 border-t border-[var(--jc-border)]/60">
          <div>
            <div className="text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.handle.panel_date_label}</div>
            <div className="text-[13px] lg:text-[12.5px] text-[var(--jc-text)]">{c.handle.panel_date_value}</div>
          </div>
          <div>
            <div className="text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.handle.panel_customer_label}</div>
            <div className="text-[13px] lg:text-[12.5px] text-[var(--jc-text)]">{c.handle.panel_customer_value}</div>
          </div>
        </div>
      </div>
      <div className="rounded-xl bg-white border border-white shadow-lg p-4" data-testid="handle-invoices">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[14.5px] lg:text-[13px] font-semibold text-[var(--jc-text)]">{c.handle.invoices_title}</div>
          <span className="text-[12.5px] lg:text-[12px] font-semibold text-[var(--jc-blue)]">{c.handle.invoices_view}</span>
        </div>
        <div className="divide-y divide-[var(--jc-border)]/60">
          {invoices.map((i) => (
            <div key={i.name} className="grid grid-cols-12 items-center py-2 gap-1.5">
              <div className="col-span-6 text-[13px] lg:text-[12.5px] text-[var(--jc-text)] truncate pr-1">{i.name}</div>
              <div className="col-span-3 text-right text-[13px] lg:text-[12.5px] font-semibold text-[var(--jc-text)]">{i.amt}</div>
              <div className="col-span-3 flex justify-end">
                <StatusPill status={i.status} kind="invoice" t={() => i.label} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <section
      data-testid="features-handle"
      className="relative lg:min-h-[1360px] isolate overflow-hidden"
      style={{ background: "#E7EEF9" }}
    >
      {/* Desktop-only environmental canvas at z-0.
          Top-anchored 960 pixel box so the entire canvas renders
          without any horizontal crop. The bottom of the section
          is the section's pale-blue background — a clean lane
          for the benefit strip that never overlaps the invoices,
          calculator, or Today's Plan notebook. */}
      <div
        className="hidden lg:block absolute inset-x-0 top-0 h-[960px] pointer-events-none"
        style={{ zIndex: 0 }}
        aria-hidden
      >
        <img
          src="/brand/features/handle_env_canvas.png"
          alt="Levi at his desk pressing a calculator with paws, invoice stack on the left, Today's Plan notebook on the right, plant, JobClose AI board with Jobs Completed / Invoices Sent / Payments Received / Profits Growing, and Built by a contractor. For contractors. wall sign."
          draggable={false}
          data-testid="handle-env-canvas"
          className="w-full h-full object-cover object-center select-none"
        />
      </div>

      {/* Content — z-2 above canvas. Full-width outer wrapper. */}
      <div className="relative w-full px-6 lg:pl-[5%] lg:pr-6 pt-16 pb-12 lg:pt-20 lg:pb-16" style={{ zIndex: 2 }}>
        <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* LEFT — pale-blue panel: headline + body + 4 cards + CTA */}
          <div className="col-span-12 lg:col-span-6" data-testid="handle-left-zone">
            <h2 className="font-display tracking-tight text-[#0A1428] text-4xl sm:text-5xl lg:text-[58px] leading-[1.02]" data-testid="handle-headline">
              {c.handle.h2a}<br /><span className="text-[var(--jc-blue)]">{c.handle.h2b}</span>
            </h2>
            <p className="mt-4 text-[16.5px] lg:text-[17px] leading-[1.55] text-[var(--jc-text-soft)] max-w-lg" data-testid="handle-body">
              {c.handle.lede}
            </p>

            <div className="mt-8 space-y-4 max-w-[540px]" data-testid="handle-cards">
              {cards.map((cc) => (
                <div
                  key={cc.testid}
                  data-testid={cc.testid}
                  className="rounded-2xl bg-white/95 border border-white shadow-md p-5 flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--jc-blue)] grid place-items-center text-white">
                    <cc.icon size={24} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[17.5px] lg:text-[16.5px] font-bold leading-tight text-[var(--jc-text)]">{cc.title}</div>
                    <div className="text-[15px] lg:text-[13.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5 lg:mt-1">{cc.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/schedule-a-demo"
              className="mt-8 jc-btn-blue text-white px-6 py-3.5 rounded-md text-[15px] font-semibold inline-flex items-center gap-2"
              data-testid="handle-cta"
            >
              {c.handle.cta} <ArrowRight size={16} />
            </Link>
          </div>

          {/* RIGHT — hugs right edge; sits over the wall-sign /
              JobClose AI board area, clear of Levi, the
              calculator, and the invoices stack. */}
          <div className="hidden lg:block lg:col-span-6" data-testid="handle-right-zone">
            <div className="ml-auto w-[300px]">
              <RightUI />
            </div>
          </div>
        </div>

        {/* Bottom benefit strip — DESKTOP ONLY. Sits in the pale
            blue lane BELOW the 960 pixel environmental image, so
            it never overlaps Levi, the calculator, or the
            notebook. One unified horizontal 5-column strip.
            Compact vertical proportion so supporting copy wraps
            naturally in 2 to 4 lines. */}
        <div className="hidden lg:block max-w-none mx-auto px-[2%] mt-12 lg:mt-[160px]">
          <div
            className="rounded-2xl bg-white/95 border border-white shadow-xl px-8 lg:px-10 py-7 lg:py-8"
            data-testid="handle-value-strip"
          >
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
              {value.map((v) => (
                <div key={v.testid} data-testid={v.testid} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                    <v.icon size={28} strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                    <div className="text-[14.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5">{v.b}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE ONLY · dedicated vertical stack — placeholder;
          will be validated in the follow-up mobile pass. */}
      <div className="lg:hidden max-w-7xl mx-auto px-6 pb-8 space-y-6" data-testid="handle-mobile-env">
        <div className="w-full h-[440px] rounded-2xl overflow-hidden shadow-xl bg-[#0B1E3E]" data-testid="handle-mobile-levi">
          <img
            src="/brand/features/handle_env_canvas.png"
            alt="Levi at his desk pressing a calculator with paws, invoice stack, Today's Plan notebook and office environment."
            draggable={false}
            className="w-full h-full object-cover select-none"
            style={{ objectPosition: "62% 55%", transform: "scale(1.28)", transformOrigin: "62% 55%" }}
          />
        </div>
        <div data-testid="handle-mobile-right-stack">
          <RightUI />
        </div>
        <div className="rounded-2xl bg-white border border-[var(--jc-border)] shadow-lg p-6 space-y-6" data-testid="handle-value-strip-mobile">
          {value.map((v) => (
            <div key={`m-${v.testid}`} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                <v.icon size={24} strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                <div className="text-[15.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5">{v.b}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
